import { useState, useCallback, useRef } from 'react';
import { prepareCatalogWhatsAppData } from '../../../utils/whatsappHelper';

/**
 * Hook que encapsula toda la lógica del carrito del catálogo público.
 * El carrito vive solo en memoria — si el usuario recarga, se pierde (intencional).
 */
export const useCatalogCart = ({ catalogInfo, setAlert }) => {
    const [cart, setCart] = useState([]);
    const [selectedProducts, setSelectedProducts] = useState(new Set());
    const [creatingOrder, setCreatingOrder] = useState(false);
    const [showCartPreview, setShowCartPreview] = useState(false);
    const [showWhatsAppModal, setShowWhatsAppModal] = useState(false);
    const [whatsappData, setWhatsappData] = useState(null);
    const [showSuccessMessage, setShowSuccessMessage] = useState(false);

    // Refs para leer estado actual sin crear dependencias en useCallback
    const cartRef = useRef(cart);
    const selectedProductsRef = useRef(selectedProducts);
    cartRef.current = cart;
    selectedProductsRef.current = selectedProducts;

    const toggleProductSelection = useCallback((product) => {
        if (selectedProductsRef.current.has(product.id)) {
            setSelectedProducts(prev => { const next = new Set(prev); next.delete(product.id); return next; });
            setCart(prev => prev.filter(item => item.productId !== product.id && item.originalProductId !== product.id));
        } else {
            setSelectedProducts(prev => { const next = new Set(prev); next.add(product.id); return next; });
        }
    }, []);

    const updateCartItem = useCallback((product, color = '', size = '', quantity = 1, unitPrice = 0, individualQuantities = '') => {
        const existingCartItem = cartRef.current.find(item => item.productId === product.id);
        const imageUrl = existingCartItem?.productImageUrl || product.imagenes?.[0] || '';

        const totalQty = typeof quantity === 'string' && quantity.includes(';')
            ? quantity.split(';').reduce((sum, q) => sum + (parseInt(q) || 0), 0)
            : parseInt(quantity) || 1;

        const item = {
            productId: product.id,
            productName: product.nombre,
            productImageUrl: imageUrl,
            colorName: color,
            sizeName: size,
            quantity,
            unitPrice,
            totalPrice: unitPrice * totalQty,
            individualQuantities: individualQuantities || quantity.toString()
        };

        if (!selectedProductsRef.current.has(product.id) && totalQty > 0) {
            setSelectedProducts(prev => { const next = new Set(prev); next.add(product.id); return next; });
        }

        setCart(prev => {
            try {
                const idx = prev.findIndex(i => i.productId === product.id);
                if (idx > -1) { const updated = [...prev]; updated[idx] = item; return updated; }
                const next = [...prev, item];
                return next;
            } catch (err) {
                console.error(`❌ [updateCartItem] Error al actualizar carrito para ${product.nombre}:`, err);
                return prev;
            }
        });
    }, []);

    const getCartItemsCount = useCallback(() => cart.length, [cart]);

    const getCartTotal = useCallback(() =>
        cart.reduce((sum, item) => sum + (item.totalPrice || 0), 0)
    , [cart]);

    const getAvailableQuantities = useCallback((product) => {
        if (!product.precios) return [];
        return Object.keys(product.precios).map(Number).sort((a, b) => a - b);
    }, []);

    const getPriceForQuantity = useCallback((product, quantity) => {
        if (!product.precios) return 0;
        if (product.precios[quantity]) return product.precios[quantity];
        const available = Object.keys(product.precios).map(Number).sort((a, b) => b - a);
        return available.length > 0 ? product.precios[available[0]] : 0;
    }, []);

    const handleOpenCartPreview = useCallback(() => {
        if (cart.length === 0) { setAlert({ type: 'error', message: 'El carrito está vacío' }); return; }
        if (!catalogInfo?.telefono) { setAlert({ type: 'error', message: 'El proveedor no tiene WhatsApp disponible' }); return; }
        setShowCartPreview(true);
    }, [cart, catalogInfo, setAlert]);

    const clearCart = useCallback(() => {
        setCart([]);
        setSelectedProducts(new Set());
    }, []);

    const createTemporaryOrder = useCallback(async () => {
        try {
            setCreatingOrder(true);
            setShowCartPreview(false);

            const orderResponse = await fetch('https://api.minymol.com/temporary-orders', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    providerId: parseInt(catalogInfo?.provider_id),
                    totalAmount: cart.reduce((sum, item) => sum + (item.totalPrice || 0), 0),
                    items: cart
                })
            });
            const { token } = await orderResponse.json();

            const shareResponse = await fetch('https://api.minymol.com/share', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    title: `Orden temporal #${token.substring(0, 8)}`,
                    imageUrl: catalogInfo?.logo_url || 'https://cdn.minymol.com/uploads/logo192.png',
                    redirectUrl: `https://ovicatalogos.com/?orden-temporal=${token}`
                })
            });
            const shareResult = await shareResponse.json();
            const shareUrl = shareResponse.ok && shareResult?.shareUrl
                ? shareResult.shareUrl
                : `https://ovicatalogos.com/?orden-temporal=${token}`;

            setWhatsappData(prepareCatalogWhatsAppData(cart, catalogInfo, shareUrl));
            setShowWhatsAppModal(true);
        } catch (error) {
            console.error('Error al crear pedido temporal:', error);
            setAlert({ type: 'error', message: 'Error al crear el pedido: ' + (error.message || 'Error desconocido') });
        } finally {
            setCreatingOrder(false);
        }
    }, [cart, catalogInfo, setAlert]);

    const handleWhatsAppConfirm = useCallback(() => {
        if (!whatsappData) return;
        window.open(`https://wa.me/${whatsappData.phoneNumber}?text=${encodeURIComponent(whatsappData.message)}`, '_blank');
        setShowWhatsAppModal(false);
        setShowSuccessMessage(true);
        setAlert({ type: 'success', message: '✓ Pedido enviado a WhatsApp exitosamente' });
        setTimeout(() => { clearCart(); setShowSuccessMessage(false); setAlert(null); }, 3000);
    }, [whatsappData, clearCart, setAlert]);

    const handleWhatsAppCancel = useCallback(() => {
        setShowWhatsAppModal(false);
        setWhatsappData(null);
    }, []);

    const handleNewOrder = useCallback(() => {
        clearCart();
        setShowSuccessMessage(false);
        setAlert(null);
    }, [clearCart, setAlert]);

    return {
        cart, setCart,
        selectedProducts, setSelectedProducts,
        creatingOrder,
        showCartPreview, setShowCartPreview,
        showWhatsAppModal,
        showSuccessMessage,
        toggleProductSelection,
        updateCartItem,
        getCartItemsCount,
        getCartTotal,
        getAvailableQuantities,
        getPriceForQuantity,
        handleOpenCartPreview,
        createTemporaryOrder,
        handleWhatsAppConfirm,
        handleWhatsAppCancel,
        handleNewOrder,
    };
};
